import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, LoadScript } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";

const MapComponent = () => {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const dropdownRef = useRef(null); // Ref for the dropdown container
  const [openDropdown, setOpenDropdown] = useState(null);

  const mapContainerStyle = {
    width: "auto",
    height: "400px",
    borderRadius: "6px",
    margin: "25px",
  };

  const initialZoom = 13;

  const markers = [
    {
      id: 1,
      lat: 42.05597321556266,
      lng: 21.36282363166994,
      label: "MKD Camera 1",
    },
    {
      id: 2,
      lat: 42.057083497696254,
      lng: 21.362176476436876,
      label: "MKD Camera 2",
    },
    {
      id: 3,
      lat: 42.172938712547925,
      lng: 21.270623592208672,
      label: "KS Sensor 1",
    },
    {
      id: 4,
      lat: 42.05289229258405,
      lng: 21.36646287387586,
      label: "MKD Sensor",
    },
    { id: 5, lat: 42.155, lng: 21.247, label: "KS Sensor 2" },
    { id: 6, lat: 42.16, lng: 21.25, label: "KS Camera 1" },
    { id: 7, lat: 42.17, lng: 21.26, label: "KS Camera 2" },
    { id: 8, lat: 42.175, lng: 21.265, label: "KS Camera 3" },
    { id: 9, lat: 42.18, lng: 21.27, label: "KS Camera 4" },
  ];

  useEffect(() => {
    // Close dropdown if clicked outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderMarkers = (map) => {
    if (window.google && window.google.maps) {
      const bounds = new window.google.maps.LatLngBounds();

      markers.forEach((marker) => {
        const position = new window.google.maps.LatLng(marker.lat, marker.lng);
        new window.google.maps.Marker({
          position: position,
          map: map,
          title: marker.label,
        });

        bounds.extend(position);
      });

      mapRef.current = map;
      map.fitBounds(bounds);
    }
  };

  const handleZoomClick = (markerId) => {
    if (mapRef.current && window.google && window.google.maps) {
      const targetMarker = markers.find((marker) => marker.id === markerId);
      if (targetMarker) {
        const targetPosition = new window.google.maps.LatLng(
          targetMarker.lat,
          targetMarker.lng
        );
        mapRef.current.setZoom(16);
        mapRef.current.panTo(targetPosition);
      }
    }
    setOpenDropdown(null);
  };

  const handleReset = () => {
    if (mapRef.current && window.google && window.google.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((marker) => {
        const position = new window.google.maps.LatLng(marker.lat, marker.lng);
        bounds.extend(position);
      });
      mapRef.current.fitBounds(bounds);
    }
    setOpenDropdown(null); // Close the dropdown when the Reset button is clicked
  };

  return (
    <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <section className="text-center pt-12 pb-20 md:px-12 lg:px-16 xl:px-24">
        <div className="flex flex-wrap justify-center items-center gap-0">
          {/* Dropdown for small screens */}
          <div className="md:hidden relative w-full max-w-xs mx-auto">
            <button
              className="px-4 py-2 bg-green-700 text-white rounded-3xl hover:bg-green-600 w-full"
              onClick={() =>
                setOpenDropdown(openDropdown === "all" ? null : "all")
              }
            >
              {t("MapComponent.allLocations")}
            </button>
            {openDropdown === "all" && (
  <div
    ref={dropdownRef} // Attach ref here
    className="absolute mt-1 rounded-lg bg-white shadow-lg z-10 text-left grid grid-cols-2 gap-2 p-2 w-full"
    style={{ left: "50%", transform: "translateX(-50%)" }}
  >
    <div>
      <h3 className="font-semibold text-gray-700">{t("MapComponent.rksCamera")}</h3>
      <button className="block w-full px-4 py-2 text-green-700 hover:bg-green-100" onClick={() => handleZoomClick(6)}>
        {t("MapComponent.rksCamera1")}
      </button>
      <button className="block w-full px-4 py-2 text-green-700 hover:bg-green-100" onClick={() => handleZoomClick(7)}>
        {t("MapComponent.rksCamera2")}
      </button>
      <button className="block w-full px-4 py-2 text-green-700 hover:bg-green-100" onClick={() => handleZoomClick(8)}>
        {t("MapComponent.rksCamera3")}
      </button>
      <button className="block w-full px-4 py-2 text-green-700 hover:bg-green-100" onClick={() => handleZoomClick(9)}>
        {t("MapComponent.rksCamera4")}
      </button>
    </div>

    <div>
      <h3 className="font-semibold text-gray-700">{t("MapComponent.rksSensor")}</h3>
      <button className="block w-full px-4 py-2 text-green-700 hover:bg-green-100" onClick={() => handleZoomClick(5)}>
        {t("MapComponent.rksSensor2")}
      </button>
    </div>

    <div>
      <h3 className="font-semibold text-gray-700">{t("MapComponent.mkdCamera")}</h3>
      <button className="block w-full px-4 py-2 text-green-700 hover:bg-green-100" onClick={() => handleZoomClick(1)}>
        {t("MapComponent.mkdCamera1")}
      </button>
      <button className="block w-full px-4 py-2 text-green-700 hover:bg-green-100" onClick={() => handleZoomClick(2)}>
        {t("MapComponent.mkdCamera2")}
      </button>
    </div>

    <div>
      <h3 className="font-semibold text-gray-700">{t("MapComponent.mkdSensor")}</h3>
      <button className="block w-full px-4 py-2 text-green-700 hover:bg-green-100" onClick={() => handleZoomClick(4)}>
        {t("MapComponent.mkdSensor1")}
      </button>
    </div>

    <div className="col-span-2">
      <button className="px-4 py-2 bg-red-600 text-white rounded-3xl w-full hover:bg-red-500" onClick={handleReset}>
        {t("MapComponent.reset")}
      </button>
    </div>
  </div>
)}
          </div>

          {/* Separate buttons for medium and larger screens */}
          <div
            className="hidden md:flex flex-wrap justify-center items-center gap-0"
            ref={dropdownRef} // Attach ref here to listen for clicks outside
          >
            {/* KS Cameras */}
            <div className="relative">
              <button
                className="px-4 py-2 bg-green-700 text-white rounded-l-3xl hover:bg-green-600"
                onClick={() =>
                  setOpenDropdown(
                    openDropdown === "KSCameras" ? null : "KSCameras"
                  )
                }
              >
                {t("MapComponent.rksCamera")}
              </button>
              {openDropdown === "KSCameras" && (
                <div className="absolute rounded-lg bg-white shadow-lg z-10 w-auto">
                  <button
                    className="block w-full px-3 py-2 rounded-lg text-green-700 hover:bg-green-100"
                    onClick={() => handleZoomClick(6)}
                  >
                    {t("MapComponent.rksCamera1")}
                  </button>
                  <button
                    className="block w-full px-3 py-2 rounded-lg text-green-700 hover:bg-green-100"
                    onClick={() => handleZoomClick(7)}
                  >
                    {t("MapComponent.rksCamera2")}
                  </button>
                  <button
                    className="block w-full px-3 py-2 rounded-lg text-green-700 hover:bg-green-100"
                    onClick={() => handleZoomClick(8)}
                  >
                    {t("MapComponent.rksCamera3")}
                  </button>
                  <button
                    className="block w-full px-3 py-2 rounded-lg text-green-700 hover:bg-green-100"
                    onClick={() => handleZoomClick(9)}
                  >
                    {t("MapComponent.rksCamera4")}
                  </button>
                </div>
              )}
            </div>

            {/* KS Sensors */}
            <div className="relative">
              <button
                className="px-4 py-2 bg-green-700 text-white hover:bg-green-600"
                onClick={() =>
                  setOpenDropdown(
                    openDropdown === "KSSensors" ? null : "KSSensors"
                  )
                }
              >
                {t("MapComponent.rksSensor")}
              </button>
              {openDropdown === "KSSensors" && (
                <div className="absolute rounded-lg bg-white shadow-lg z-10 w-auto">
                  <button
                    className="block w-full px-3 py-2 rounded-lg text-green-700 hover:bg-green-100"
                    onClick={() => handleZoomClick(3)}
                  >
                    {t("MapComponent.rksSensor1")}
                  </button>
                  <button
                    className="block w-full px-3 py-2 rounded-lg text-green-700 hover:bg-green-100"
                    onClick={() => handleZoomClick(5)}
                  >
                    {t("MapComponent.rksSensor2")}
                  </button>
                </div>
              )}
            </div>
            {/* MKD Cameras */}
            <div className="relative">
              <button
                className="px-4 py-2 bg-green-700 text-white hover:bg-green-600"
                onClick={() =>
                  setOpenDropdown(
                    openDropdown === "MKDCameras" ? null : "MKDCameras"
                  )
                }
              >
                {t("MapComponent.mkdCamera")}
              </button>
              {openDropdown === "MKDCameras" && (
                <div className="absolute rounded-lg bg-white shadow-lg z-10 w-auto">
                  <button
                    className="block w-full px-3 py-2 rounded-lg text-green-700 hover:bg-green-100"
                    onClick={() => handleZoomClick(1)}
                  >
                    {t("MapComponent.mkdCamera1")}
                  </button>
                  <button
                    className="block w-full px-3 py-2 rounded-lg text-green-700 hover:bg-green-100"
                    onClick={() => handleZoomClick(2)}
                  >
                    {t("MapComponent.mkdCamera2")}
                  </button>
                </div>
              )}
            </div>

            {/* MKD Sensors */}
            <div className="relative">
              <button
                className="px-4 py-2 bg-green-700 text-white hover:bg-green-600"
                onClick={() =>
                  setOpenDropdown(
                    openDropdown === "MKDSensors" ? null : "MKDSensors"
                  )
                }
              >
                {t("MapComponent.mkdSensor")}
              </button>
              {openDropdown === "MKDSensors" && (
                <div className="absolute rounded-lg bg-white shadow-lg z-10 w-auto">
                  <button
                    className="block w-full px-4 py-2 rounded-lg text-green-700 hover:bg-green-100"
                    onClick={() => handleZoomClick(4)}
                  >
                    {t("MapComponent.mkdSensor1")}
                  </button>
                </div>
              )}
            </div>

            {/* Reset Map button */}
            <div className="relative">
              <button
                className="px-4 py-2 bg-red-600 text-white md:rounded-r-3xl hover:bg-red-500 w-full"
                onClick={handleReset}
              >
                {t("MapComponent.reset")}
              </button>
            </div>
          </div>
        </div>

        {/* Map Container */}
        <div className="relative flex justify-center items-center md:px-7 mt-10 mb-8">
          <div className=" w-full rounded-md bg-gradient-to-r from-green-800 to-green-500">
            <div className="">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={initialZoom}
                mapTypeId="hybrid"
                onLoad={renderMarkers}
              />
            </div>
          </div>
        </div>
      </section>
    </LoadScript>
  );
};

export default MapComponent;
